import * as THREE from 'three';
import { materialPool } from './MaterialPool';

// ============================================================
// Cinematic explosion — fireball + debris + shockwave + smoke
// ============================================================

const FIREBALL_COUNT = 40;
const DEBRIS_COUNT = 20;
const SMOKE_COUNT = 15;
const EXPLOSION_LIFETIME = 1.8;
const FIREBALL_SPEED = 100;
const DEBRIS_SPEED = 150;

interface Particle {
  readonly mesh: THREE.Mesh;
  readonly velocity: THREE.Vector3;
  readonly type: 'fire' | 'debris' | 'smoke' | 'flash';
  age: number;
  lifetime: number;
}

const fireGeom = new THREE.SphereGeometry(1.2, 5, 5);
const debrisGeom = new THREE.BoxGeometry(0.6, 0.6, 0.6);
const smokeGeom = new THREE.SphereGeometry(3, 6, 6);
const flashGeom = new THREE.SphereGeometry(1, 8, 8);

export class ExplosionSystem {
  private readonly scene: THREE.Scene;
  private readonly particles: Particle[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawn(position: THREE.Vector3): void {
    // Initial flash — bright white sphere that expands and fades instantly
    const flashMat = materialPool.acquire();
    flashMat.color.set(0xffffff);
    const flash = new THREE.Mesh(flashGeom, flashMat);
    flash.position.copy(position);
    flash.frustumCulled = false;
    this.scene.add(flash);
    this.particles.push({
      mesh: flash, velocity: new THREE.Vector3(), type: 'flash',
      age: 0, lifetime: 0.3,
    });

    // Point light for the flash
    const light = new THREE.PointLight(0xff6600, 10, 200);
    light.position.copy(position);
    this.scene.add(light);
    // Remove light after a short time
    setTimeout(() => this.scene.remove(light), 500);

    // Fireball particles — orange/red/yellow
    for (let i = 0; i < FIREBALL_COUNT; i++) {
      const hue = 0.02 + Math.random() * 0.1;
      const lightness = 0.4 + Math.random() * 0.3;
      const color = new THREE.Color().setHSL(hue, 1, lightness);

      const material = materialPool.acquire();
      material.color.copy(color);

      const mesh = new THREE.Mesh(fireGeom, material);
      mesh.position.copy(position);
      mesh.frustumCulled = false;
      this.scene.add(mesh);

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.3) * 2, // bias upward
        (Math.random() - 0.5) * 2,
      ).normalize().multiplyScalar(FIREBALL_SPEED * (0.3 + Math.random() * 0.7));

      this.particles.push({
        mesh, velocity, type: 'fire',
        age: 0, lifetime: EXPLOSION_LIFETIME * (0.5 + Math.random() * 0.5),
      });
    }

    // Dark debris chunks — fast, small
    for (let i = 0; i < DEBRIS_COUNT; i++) {
      const material = materialPool.acquire();
      material.color.setHSL(0, 0, Math.random() * 0.3);

      const mesh = new THREE.Mesh(debrisGeom, material);
      mesh.position.copy(position);
      mesh.frustumCulled = false;
      this.scene.add(mesh);

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
      ).normalize().multiplyScalar(DEBRIS_SPEED * (0.4 + Math.random() * 0.6));

      this.particles.push({
        mesh, velocity, type: 'debris',
        age: 0, lifetime: EXPLOSION_LIFETIME * (0.6 + Math.random() * 0.4),
      });
    }

    // Smoke — large, slow, dark grey, rises
    for (let i = 0; i < SMOKE_COUNT; i++) {
      const grey = 0.15 + Math.random() * 0.15;
      const material = materialPool.acquire();
      material.color.setRGB(grey, grey, grey);
      material.opacity = 0.5;

      const mesh = new THREE.Mesh(smokeGeom, material);
      mesh.position.copy(position).add(new THREE.Vector3(
        (Math.random() - 0.5) * 10,
        Math.random() * 5,
        (Math.random() - 0.5) * 10,
      ));
      mesh.frustumCulled = false;
      this.scene.add(mesh);

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 15,
        10 + Math.random() * 20,
        (Math.random() - 0.5) * 15,
      );

      this.particles.push({
        mesh, velocity, type: 'smoke',
        age: 0, lifetime: EXPLOSION_LIFETIME * 1.5,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;

      if (p.age >= p.lifetime) {
        this.scene.remove(p.mesh);
        materialPool.release(p.mesh.material as THREE.MeshBasicMaterial);
        this.particles.splice(i, 1);
        continue;
      }

      const t = p.age / p.lifetime;

      if (p.type === 'flash') {
        // Rapid expand and fade
        (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t * t;
        p.mesh.scale.setScalar(1 + t * 30);
      } else if (p.type === 'fire') {
        p.mesh.position.addScaledVector(p.velocity, dt);
        p.velocity.multiplyScalar(1 - 3 * dt);
        (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
        // Shift color from bright to dark red
        const hue = 0.08 * (1 - t);
        (p.mesh.material as THREE.MeshBasicMaterial).color.setHSL(hue, 1, 0.5 * (1 - t * 0.5));
        p.mesh.scale.setScalar(1 + t * 3);
      } else if (p.type === 'debris') {
        p.mesh.position.addScaledVector(p.velocity, dt);
        p.velocity.y -= 80 * dt; // gravity
        p.mesh.rotation.x += 10 * dt;
        p.mesh.rotation.z += 8 * dt;
        (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
      } else if (p.type === 'smoke') {
        p.mesh.position.addScaledVector(p.velocity, dt);
        p.velocity.multiplyScalar(1 - 1.5 * dt);
        (p.mesh.material as THREE.MeshBasicMaterial).opacity = 0.5 * (1 - t);
        p.mesh.scale.setScalar(1 + t * 4);
      }
    }
  }

  dispose(): void {
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      materialPool.release(p.mesh.material as THREE.MeshBasicMaterial);
    }
    this.particles.length = 0;
  }
}
